'use client'

import { useEffect, useState } from 'react'
import Script from 'next/script'
import Link from '@/components/ui/NavLink'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Cookie } from 'lucide-react'
import { useLang } from '@/lib/LanguageContext'
import { ButtonPrimary } from '@/components/ui/ButtonPrimary'
import { ButtonOutline } from '@/components/ui/ButtonOutline'

const GA_MEASUREMENT_ID = 'G-JPXQ2QF2Z6'
const STORAGE_KEY = 'aviva-cookie-consent'
export const OPEN_COOKIE_SETTINGS_EVENT = 'aviva:open-cookie-settings'
/** Scroll distance (px) that collapses (down) or restores (up) an undecided banner. */
const SCROLL_TOGGLE_DISTANCE = 80

// `custom` = true when the banner travels to/from the bottom-left cookie button.
// The wrapper spans the viewport width, so scaling from its bottom-left lands on the button.
const bannerVariants = {
  hidden: (viaCorner: boolean) =>
    viaCorner ? { opacity: 0, scale: 0.06, y: 0 } : { opacity: 0, scale: 1, y: 24 },
  shown: { opacity: 1, scale: 1, y: 0 },
}

/** Optional cookie categories. Add new ones (e.g. marketing) here and in i18n `cookies.categories`. */
type Preferences = { analytics: boolean }

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

/** Opens the cookie preferences panel so visitors can change their choice. */
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))
}

function readPreferences(): Preferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed?.analytics === 'boolean' ? { analytics: parsed.analytics } : null
  } catch {
    return null
  }
}

function savePreferences(prefs: Preferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {}
}

// GA sets _ga / _ga_<ID> on the registrable domain; clear every variant on withdrawal.
function clearAnalyticsCookies() {
  const host = window.location.hostname
  const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`]
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (!name.startsWith('_ga')) return
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`
    })
  })
}

function Toggle({
  checked,
  disabled,
  onChange,
  labelledBy,
}: {
  checked: boolean
  disabled?: boolean
  onChange?: (value: boolean) => void
  labelledBy: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative shrink-0 w-11 h-6 rounded-pill transition-colors duration-150
        ${checked ? 'bg-accent-pink' : 'bg-white/15'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-150
          ${checked ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  )
}

export default function CookieConsent() {
  const { t } = useLang()
  const [prefs, setPrefs] = useState<Preferences | null>(null)
  const [draft, setDraft] = useState<Preferences>({ analytics: false })
  const [open, setOpen] = useState(false)
  const [customizing, setCustomizing] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [viaCorner, setViaCorner] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    const saved = readPreferences()
    setPrefs(saved)
    if (!saved) setOpen(true)

    const reopen = () => {
      setDraft(readPreferences() ?? { analytics: false })
      setCustomizing(true)
      setMinimized(false)
      setViaCorner(false)
      setOpen(true)
    }
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen)
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen)
  }, [])

  // Undecided visitor: scrolling down tucks the banner into the bottom-left button,
  // scrolling back up brings it back as a reminder to choose.
  // Skipped while customizing, and when reopened from the footer (a choice already exists).
  const undecided = open && !customizing && !prefs
  useEffect(() => {
    if (!undecided) return
    // Turning point: smallest scrollY while expanded, largest while minimized — distance is measured from there.
    let anchor = window.scrollY
    const onScroll = () => {
      const y = window.scrollY
      if (minimized) {
        anchor = Math.max(anchor, y)
        if (anchor - y >= SCROLL_TOGGLE_DISTANCE) setMinimized(false)
      } else {
        anchor = Math.min(anchor, y)
        if (y - anchor >= SCROLL_TOGGLE_DISTANCE) {
          setViaCorner(true)
          setMinimized(true)
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [undecided, minimized])

  const apply = (next: Preferences) => {
    savePreferences(next)
    setPrefs(next)
    setOpen(false)
    setMinimized(false)
    setViaCorner(false)
    setCustomizing(false)
    window.gtag?.('consent', 'update', { analytics_storage: next.analytics ? 'granted' : 'denied' })
    if (!next.analytics) clearAnalyticsCookies()
  }

  const acceptAll = () => apply({ analytics: true })
  const rejectAll = () => apply({ analytics: false })

  const categories = [
    { key: 'necessary', ...t.cookies.categories.necessary, checked: true, locked: true },
    { key: 'analytics', ...t.cookies.categories.analytics, checked: draft.analytics, locked: false },
  ] as const

  return (
    <>
      {/* Google Analytics 4 — loaded only after the visitor consents to analytics */}
      {prefs?.analytics && (
        <>
          <Script id="google-analytics-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
              gtag('consent', 'default', {
                analytics_storage: 'granted',
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied'
              });
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}');
            `}
          </Script>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
        </>
      )}

      <AnimatePresence custom={viaCorner}>
        {open && !minimized && (
          <motion.div
            key="cookie-banner"
            custom={viaCorner}
            variants={bannerVariants}
            initial="hidden"
            animate="shown"
            exit="hidden"
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            style={{ transformOrigin: '0% 100%' }}
            className="fixed bottom-4 inset-x-4 z-[60] flex justify-center pointer-events-none"
          >
          <div
            // Measured by lib/bottom-bar-lift.ts so the chat launcher rides above it
            data-bottom-bar=""
            role="dialog"
            aria-live="polite"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-body"
            className="pointer-events-auto w-full max-w-[1000px]
              max-h-[calc(100dvh-2rem)] overflow-y-auto
              rounded-[20px] border border-white/10 bg-[#111118]/90 backdrop-blur-xl
              p-4 sm:p-6 lg:px-8 shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
          >
            <h2
              id="cookie-consent-title"
              className="font-display font-semibold text-white text-[15px] sm:text-lg mb-1.5 sm:mb-2"
            >
              {customizing ? t.cookies.customizeTitle : t.cookies.title}
            </h2>
            <p id="cookie-consent-body" className="text-white/60 text-[13px] sm:text-sm leading-snug sm:leading-relaxed">
              {customizing ? t.cookies.customizeBody : t.cookies.body}{' '}
              <Link
                href="/privacy"
                className="text-white/80 hover:text-white underline underline-offset-2 decoration-white/30 transition-colors duration-150"
              >
                {t.cookies.policyLink}
              </Link>
            </p>

            {customizing && (
              <ul className="mt-4 grid gap-3 md:grid-cols-2">
                {categories.map((c) => (
                  <li
                    key={c.key}
                    className="flex items-start justify-between gap-4 rounded-[14px] border border-white/[0.08] bg-white/[0.04] p-4"
                  >
                    <div>
                      <p id={`cookie-cat-${c.key}`} className="font-display font-semibold text-white text-sm">
                        {c.title}
                      </p>
                      <p className="mt-1 text-white/50 text-xs leading-relaxed">{c.description}</p>
                    </div>
                    <Toggle
                      checked={c.checked}
                      disabled={c.locked}
                      labelledBy={`cookie-cat-${c.key}`}
                      onChange={(value) => setDraft((d) => ({ ...d, [c.key]: value }))}
                    />
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 sm:mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-1 sm:gap-3">
              {customizing ? (
                <button
                  type="button"
                  onClick={() => setCustomizing(false)}
                  className="sm:mr-auto min-h-[44px] text-sm text-white/60 hover:text-white transition-colors duration-150 cursor-pointer"
                >
                  ← {t.cookies.back}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setDraft(prefs ?? { analytics: false })
                    setCustomizing(true)
                  }}
                  className="sm:mr-auto min-h-[44px] text-sm text-white/60 hover:text-white underline underline-offset-4
                    decoration-white/25 hover:decoration-white/60 transition-colors duration-150 cursor-pointer"
                >
                  {t.cookies.customize}
                </button>
              )}
              <div className={customizing ? 'flex flex-col sm:flex-row gap-3' : 'flex gap-3'}>
                {customizing ? (
                  <>
                    <ButtonOutline size="sm" className="sm:min-w-[170px] justify-center" onClick={rejectAll}>
                      {t.cookies.rejectAll}
                    </ButtonOutline>
                    <ButtonOutline size="sm" className="sm:min-w-[170px] justify-center" onClick={() => apply(draft)}>
                      {t.cookies.save}
                    </ButtonOutline>
                    <ButtonPrimary size="sm" className="sm:min-w-[170px] justify-center" onClick={acceptAll}>
                      {t.cookies.acceptAll}
                    </ButtonPrimary>
                  </>
                ) : (
                  <>
                    <ButtonOutline size="sm" className="flex-1 sm:flex-none sm:min-w-[170px] justify-center" onClick={rejectAll}>
                      {t.cookies.reject}
                    </ButtonOutline>
                    <ButtonPrimary size="sm" className="flex-1 sm:flex-none sm:min-w-[170px] justify-center" onClick={acceptAll}>
                      {t.cookies.accept}
                    </ButtonPrimary>
                  </>
                )}
              </div>
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed banner — reopens it on click */}
      <AnimatePresence>
        {open && minimized && (
          <motion.button
            key="cookie-button"
            type="button"
            onClick={() => setMinimized(false)}
            aria-label={t.cookies.settings}
            title={t.cookies.settings}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1, transition: { delay: 0.2, type: 'spring', stiffness: 380, damping: 22 } }}
            exit={{ opacity: 0, scale: 0.4, transition: { duration: 0.15 } }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            className="fixed bottom-4 left-4 z-[60] w-14 h-14 rounded-full flex items-center justify-center cursor-pointer"
          >
            <motion.span
              aria-hidden
              className="absolute -inset-1.5 rounded-full blur-md"
              style={{ background: 'linear-gradient(135deg, #E040A0 0%, #FFB76C 100%)' }}
              animate={reduceMotion ? { opacity: 0.55 } : { opacity: [0.35, 0.85, 0.35], scale: [0.95, 1.1, 0.95] }}
              transition={reduceMotion ? undefined : { duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span className="absolute inset-0 rounded-full border border-white/15 bg-[#111118]/95 backdrop-blur-xl" />
            <Cookie
              aria-hidden
              strokeWidth={1.75}
              className="relative w-6 h-6 text-accent-orange drop-shadow-[0_0_6px_rgba(255,183,108,0.9)]"
            />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  )
}
